// Desktop Property Details Panel - displays property information in side panel
import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { DesktopPanelBody } from '@/components/common/admin/desktop/panel/DesktopPanelBody';
import { IProperty } from '@/types/admin/properties';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useLastDefined } from '@/hooks/common/useLastDefined';

interface DesktopPropertyDetailsPanelProps {
  visible: boolean;
  /** Undefined once the host has cleared the selection; the panel keeps the last one while it slides out. */
  property?: IProperty;
  onClose: () => void;
}

export const DesktopPropertyDetailsPanel: React.FC<DesktopPropertyDetailsPanelProps> = ({
  visible,
  property: selectedProperty,
  onClose,
}) => {
  const styles = useStyles();
  // The host keeps this panel mounted so it can slide in and out, and clears
  // the selection on close; hold the last property for the slide-out.
  const property = useLastDefined(selectedProperty);
  return (
    <DesktopSidePanel
      visible={visible}
      onClose={onClose}
      width={520}
    >
      {property && (
      <>
      <DesktopPanelHeader
        title={property.marketingName}
        onClose={onClose}
      />

      <DesktopPanelBody scroll>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>General Information</Text>
          
          <View style={styles.field}>
            <Text style={styles.label}>Name</Text>
            <Text style={styles.value}>{property.marketingName}</Text>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>External ID</Text>
            <Text style={styles.value}>{property.externalId}</Text>
          </View>

          {property.address && (
            <View style={styles.field}>
              <Text style={styles.label}>Address</Text>
              <Text style={styles.value}>{property.address}</Text>
            </View>
          )}

          {property.organizationId && (
            <View style={styles.field}>
              <Text style={styles.label}>Organization ID</Text>
              <Text style={styles.value}>{property.organizationId}</Text>
            </View>
          )}

          <View style={styles.field}>
            <Text style={styles.label}>PMS Type</Text>
            <Text style={styles.value}>{property.pmsType || 'Yardi'}</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Contact Information</Text>
          
          <View style={styles.field}>
            <Text style={styles.label}>Email</Text>
            <Text style={styles.value}>{property.propertyEmailAddress || '—'}</Text>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Phone</Text>
            <Text style={styles.value}>{property.propertyPhoneNumber || '—'}</Text>
          </View>

          {property.propertyUrl && (
            <View style={styles.field}>
              <Text style={styles.label}>Website</Text>
              <Text style={styles.value}>{property.propertyUrl}</Text>
            </View>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Fee Configuration</Text>
          
          <View style={styles.field}>
            <Text style={styles.label}>NSF Fee</Text>
            <Text style={styles.value}>${property.propertyNsfFee || 0}</Text>
          </View>

          {property.propertyLateDayStart !== undefined && (
            <View style={styles.field}>
              <Text style={styles.label}>Late Day Start</Text>
              <Text style={styles.value}>Day {property.propertyLateDayStart}</Text>
            </View>
          )}

          <View style={styles.field}>
            <Text style={styles.label}>Late Day Fee</Text>
            <Text style={styles.value}>${property.propertyLateDayFee || 0}</Text>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Late Daily Fee</Text>
            <Text style={styles.value}>${property.propertyLateDailyFee || 0}</Text>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Max Monthly Fee</Text>
            <Text style={styles.value}>${property.propertyMaxMonthlyFee || 0}</Text>
          </View>
        </View>

        {(property.creditCardMerchantAccountId || property.achMerchantAccountId) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Merchant Accounts</Text>
            
            {property.creditCardMerchantAccountId && (
              <View style={styles.field}>
                <Text style={styles.label}>Credit Card Merchant ID</Text>
                <Text style={styles.value}>{property.creditCardMerchantAccountId}</Text>
              </View>
            )}

            {property.achMerchantAccountId && (
              <View style={styles.field}>
                <Text style={styles.label}>ACH Merchant ID</Text>
                <Text style={styles.value}>{property.achMerchantAccountId}</Text>
              </View>
            )}
          </View>
        )}
      </DesktopPanelBody>
      </>
      )}
    </DesktopSidePanel>
  );
};

const useStyles = () =>
  useThemedStyles(({ colors, components }) =>
    StyleSheet.create({
      section: {
        marginBottom: 32,
      },
      sectionTitle: {
        fontSize: 14,
        fontWeight: '600',
        color: colors.text.primary,
        marginBottom: 16,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginTop: 16,
      },
      field: {
        marginBottom: 16,
      },
      label: {
        fontSize: 12,
        fontWeight: '500',
        color: components.admin.formInput.label,
        marginBottom: 4,
      },
      value: {
        fontSize: 14,
        color: colors.text.primary,
        lineHeight: 20,
      },
    }),
  );

